<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { questionTypes } from '../../stores/questionsetStore';
  
  const dispatch = createEventDispatcher();
  
  let newQuestionType = 'Text';
  let newQuestionRelation = 'selfNode';
  
  // Real relationship types based on the documentation
  const relationTypes = {
    Text: ['selfNode', 'createNodes', 'selfProps', 'addProps'],
    CheckBox: ['createNodes', 'selfProps', 'addProps'],
    Select: ['createNodes', 'selfProps', 'addProps'],
    Quant: ['selfProps', 'addProps'],
    Map: ['selfProps', 'addProps'],
    DateTime: ['selfProps', 'addProps'],
  };
  
  const relationDescriptions = {
    selfNode: "Create or update the self node with setSelf + selfProps",
    createNodes: "Create new nodes connected to self or other nodes",
    selfProps: "Add properties directly to the self node",
    addProps: "Add properties to existing nodes from other questions",
  };
  
  // Helper to determine if a relationship creates a props question
  function isPropsRelation(relation) {
    return relation === 'selfProps' || relation === 'addProps';
  }
  
  // Get appropriate encryption for the relationship type
  function getEncryptionForRelation(relation) {
    if (isPropsRelation(relation)) {
      return ["none"]; // Props questions get no encryption
    } else {
      return ["project", "consent"]; // Regular questions get full encryption
    }
  }
  
  // Update relation options when type changes
  $: {
    if (!relationTypes[newQuestionType].includes(newQuestionRelation)) {
      newQuestionRelation = relationTypes[newQuestionType][0];
    }
  }
  
  function handleKeydown(event) {
    if (event.key === 'Escape') {
      dispatch('close');
    }
  }
  
  function handleCreateDraft() {
    // Dispatch event to create draft question and open edit panel
    dispatch('createDraft', {
      type: newQuestionType,
      relationshipType: newQuestionRelation,
      encryption: getEncryptionForRelation(newQuestionRelation)
    });
  }
  
  function handleClose() {
    dispatch('close');
  }
  
  onMount(() => {
    const keydownHandler = (event) => {
      if (event.key === 'Escape') {
        dispatch('close');
      }
    };
    
    document.addEventListener('keydown', keydownHandler);
    
    return () => {
      document.removeEventListener('keydown', keydownHandler);
    };
  });
</script>

<div class="modal-overlay" on:click={handleClose}>
  <div class="modal-content" on:click|stopPropagation>
    <h3>Add New Question</h3>
    
    <div class="form-styled">
      <div class="field-wrapper">
        <label>Question Type</label>
        <select bind:value={newQuestionType}>
          {#each questionTypes as type}
            <option value={type}>{type}</option>
          {/each}
        </select>
      </div>
      
      <div class="field-wrapper">
        <label>Relationship Type</label>
        <select bind:value={newQuestionRelation}>
          {#each relationTypes[newQuestionType] as relation}
            <option value={relation}>{relation}</option>
          {/each}
        </select>
        
        <p class="relationship-description">
          {relationDescriptions[newQuestionRelation]}
        </p>
        
        {#if isPropsRelation(newQuestionRelation)}
          <p class="encryption-note">
            🔒 This will be a props question with no encryption
          </p>
        {:else}
          <p class="encryption-note">
            🔐 This will have project + consent encryption
          </p>
        {/if}
      </div>
      
      <div class="modal-actions">
        <button class="btn" on:click={handleClose}>
          Cancel
        </button>
        <button class="btn btn-primary" on:click={handleCreateDraft}>
          Create Draft
        </button>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
  }
  
  .modal-content {
    background: #f6f6f6;
    border-radius: 10px;
    padding: 20px;
    width: 450px;
    max-width: 90%;
    border: 4px solid #d4d3bc;
    
    h3 {
      margin-bottom: 15px;
    }
  }
  
  .relationship-description {
    font-size: 11px;
    color: #6b7280;
    margin-top: 5px;
    font-style: italic;
  }
  
  .encryption-note {
    font-size: 11px;
    color: #0369a1;
    margin-top: 8px;
    padding: 6px 8px;
    background: #f0f9ff;
    border-radius: 4px;
    border: 1px solid #0ea5e9;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
    
    .btn {
      margin: 0;
    }
  }
  
  .btn-primary {
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  }
</style>